/**
 * Service Helper Utilities
 * 
 * Shared utility functions for service-related calculations and checks.
 * Used across Contracts, Milestones, and other financial modules to ensure
 * consistent behavior.
 */

/**
 * Check if a service is active for a given month.
 * 
 * A service is considered ACTIVE for a month if:
 * - No cancellation date is set, OR
 * - The month is BEFORE the cancellation effective month
 * 
 * Boundary behavior: If ym === cancellationEffectiveYm, the service is CANCELLED
 * (i.e., the cancellation takes effect AT THE START of that month)
 * 
 * @param {Object} service - Service object with cancellationEffectiveYm field
 * @param {string} ym - Year-month string in YYYY-MM format
 * @returns {boolean} True if service is active for this month
 * 
 * @example
 * const service = { name: 'SEO', cancellationEffectiveYm: '2026-06' };
 * isServiceActiveForMonth(service, '2026-05'); // true  - before cancellation
 * isServiceActiveForMonth(service, '2026-06'); // false - at cancellation
 * isServiceActiveForMonth(service, '2026-07'); // false - after cancellation
 */
export const isServiceActiveForMonth = (service, ym) => {
  if (!service) {
    console.warn('[serviceHelpers][isServiceActiveForMonth] Service is null/undefined');
    return false;
  }
  
  // No cancellation date = always active
  if (!service.cancellationEffectiveYm) return true;
  
  // Service is active if month is BEFORE cancellation month
  // String comparison works for YYYY-MM format: "2026-01" < "2026-02" = true
  const isActive = String(ym) < String(service.cancellationEffectiveYm);
  
  return isActive;
};

/**
 * Check if a frequency is monthly.
 * Handles various formats: "monthly", "Monthly", "MONTHLY", etc.
 * 
 * @param {string} frequency - Frequency string
 * @returns {boolean} True if frequency is monthly
 */
export const isMonthlyFrequency = (frequency) => {
  const freq = String(frequency || '').toLowerCase();
  return freq === 'monthly' || freq.includes('month');
};

/**
 * Check if a frequency is one-time.
 * 
 * @param {string} frequency - Frequency string
 * @returns {boolean} True if frequency is one-time
 */
export const isOneTimeFrequency = (frequency) => {
  const freq = String(frequency || '').toLowerCase();
  return freq === 'one-time' || freq === 'one_time' || freq === 'onetime';
};

/**
 * Get active monthly services for a given month.
 * Filters services by:
 * - Monthly frequency
 * - Active for the given month (respects cancellationEffectiveYm)
 * 
 * @param {Array} services - Array of service objects
 * @param {string} ym - Year-month string in YYYY-MM format
 * @returns {Array} Array of active monthly services
 */
export const getActiveMonthlyServices = (services, ym) => {
  if (!Array.isArray(services)) return [];
  
  return services.filter(service => 
    service && 
    isMonthlyFrequency(service.frequency) && 
    isServiceActiveForMonth(service, ym)
  );
};

/**
 * Get one-time services that should be applied to a specific milestone.
 * Matches by oneTimeAppliedMilestoneId or includes unapplied one-time services.
 * 
 * @param {Array} services - Array of service objects
 * @param {string} milestoneId - Milestone ID to match
 * @param {boolean} includeUnapplied - Include services not yet applied
 * @returns {Array} Array of one-time services for this milestone
 */
export const getOneTimeServicesForMilestone = (services, milestoneId, includeUnapplied = false) => {
  if (!Array.isArray(services)) return [];
  
  return services.filter(service => {
    if (!service) return false;
    if (!isOneTimeFrequency(service.frequency)) return false;
    
    // Match by milestone ID
    if (service.oneTimeAppliedMilestoneId === milestoneId) return true;
    
    // Include unapplied if requested
    if (includeUnapplied && !service.oneTimeApplied) return true;
    
    return false;
  });
};

/**
 * Calculate total revenue for services.
 * 
 * @param {Array} services - Array of service objects
 * @returns {number} Total revenue (sum of unitPrice * quantity)
 */
export const calculateServiceRevenue = (services) => {
  if (!Array.isArray(services)) return 0;
  
  return services.reduce((sum, service) => {
    const unitPrice = Number(service?.unitPrice) || 0;
    const quantity = Number(service?.quantity) || 1;
    return sum + (unitPrice * quantity);
  }, 0);
};

/**
 * Calculate total internal cost for services.
 * 
 * @param {Array} services - Array of service objects
 * @returns {number} Total internal cost (sum of internalUnitCost * quantity)
 */
export const calculateServiceInternalCost = (services) => {
  if (!Array.isArray(services)) return 0;
  
  return services.reduce((sum, service) => {
    const internalCost = Number(service?.internalUnitCost) || 0;
    const quantity = Number(service?.quantity) || 1;
    return sum + (internalCost * quantity);
  }, 0);
};

/**
 * Round to 2 decimal places (cents precision).
 * 
 * @param {number} value - Value to round
 * @returns {number} Rounded value
 */
export const round2 = (value) => {
  return Math.round((Number(value) || 0) * 100) / 100;
};

/**
 * Compare amounts by cents to avoid floating point issues.
 * 
 * @param {number} a - First amount
 * @param {number} b - Second amount
 * @returns {boolean} True if amounts are equal (by cents)
 */
export const amountsEqual = (a, b) => {
  const centsA = Math.round((Number(a) || 0) * 100);
  const centsB = Math.round((Number(b) || 0) * 100);
  return centsA === centsB;
};
